import type { Theme, WithStyles } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'
import React, { Suspense, useContext } from 'react'
import ErrorIcon from '@material-ui/icons/Error'
import * as Sentry from '@sentry/react'
import { Backdrop } from '@paintscout/ui/components/Dialog'
import type { DialogProps } from '@paintscout/ui/components/Dialog'
import { usePrevious } from '@paintscout/ui/hooks'
import { AlertDialog, withTranslation } from '@paintscout/ui'
import type { WithTranslation } from '@paintscout/ui'
import DialogStackContext from './DialogStackContext'
import type { WithDialogStackContext } from './withDialogStackContext'
import withDialogStackContext from './withDialogStackContext'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'

const styles = (theme: Theme) =>
  createStyles({
    backdrop: {
      zIndex: theme.zIndex.modal
    }
  })

export interface DialogStackRendererProps extends WithStyles<typeof styles> {}

function DialogStackRenderer({ classes }: DialogStackRendererProps, ref?: React.Ref<HTMLDivElement>) {
  const { stack } = useContext(DialogStackContext)
  const isOpen = stack.length > 0

  // Prevent background scrolling when a dialog is open. MUI's built-in scroll lock is disabled
  // (disableScrollLock), so we handle it ourselves via the shared hook (position:fixed on body
  // plus an html-background tint so the scrollbar gutter matches the backdrop with no shift).
  useBodyScrollLock(isOpen)

  const prevStackLength = usePrevious(stack.length)
  const currentDialog = stack.at(-1)

  return (
    <DialogErrorBoundary>
      <div
        ref={ref}
        data-testid="dialog-stack"
        style={{
          pointerEvents: stack.length > 0 && !currentDialog?.props?.hideBackdrop ? 'auto' : undefined
        }}
      >
        <Backdrop
          className={classes.backdrop}
          open={stack.length > 0 && !currentDialog?.props?.hideBackdrop}
          transitionDuration={225}
        />
        {stack.map((dialog, index) => {
          const DialogComponent = dialog.component as React.ComponentType<DialogProps>

          return (
            <Suspense key={index} fallback={null}>
              <DialogComponent
                open={dialog === currentDialog}
                keepMounted={true}
                transitionDuration={prevStackLength === 0 ? 225 : 0}
                {...dialog.props}
                hideBackdrop
              >
                {dialog.props.children}
              </DialogComponent>
            </Suspense>
          )
        })}
      </div>
    </DialogErrorBoundary>
  )
}

const DialogErrorBoundary = withDialogStackContext(
  withTranslation(
    class DialogErrorBoundary extends React.Component<WithDialogStackContext & WithTranslation> {
      // there will be a warning about using getDerivedStateFromError and showing an error message instead of the children
      // but we just want to dismiss the dialogs
      public componentDidCatch(error: any, info: any) {
        console.log(info)
        console.log(error)
        const currentDialog = this.props.dialogStackContext.stack.at(-1)
        console.log({ currentDialog, component: currentDialog.component })

        Sentry.captureException(error, {
          tags: {
            crash: 'dialog'
          }
        })
        const { __ } = this.props.translation
        this.props.dialogStackContext.dismissAllDialogs()
        setTimeout(() =>
          this.props.dialogStackContext.openDialog(AlertDialog, {
            icon: <ErrorIcon />,
            title: __('An unexpected error occurred'),
            message: __('We are sorry for the inconvenience'),
            onConfirm: this.props.dialogStackContext.dismissDialog
          })
        )
      }

      public render() {
        return this.props.children
      }
    }
  )
)

export default withStyles(styles)(React.forwardRef(DialogStackRenderer))
